import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate as formatDateUtil } from '../utils/format';
import { ensureLogin, fetchProjects, createProject, updateProject, deleteProject, fetchUsers, uploadFile } from '../utils/api';

// Removido: dados simulados. Vamos usar a API real.

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProject, setCurrentProject] = useState({
    title: '',
    status: 'kickoff',
    startDate: '',
    endDate: '',
    client: '',
    budget: '',
    clientContactName: '',
    clientContactEmail: '',
    clientContactPhone: '',
    isqCommercialName: '',
    isqCommercialEmail: '',
    isqCommercialPhone: '',
    proposalPath: '',
    kickoffMeetingConfirmed: false,
    contractConfirmed: false,
    managerId: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [projectErrors, setProjectErrors] = useState({});

  useEffect(() => {
    const load = async () => {
      await ensureLogin();
      const data = await fetchProjects();
      setProjects(data);
      try {
        const allUsers = await fetchUsers();
        setUsers(allUsers || []);
      } catch (e) {
        setUsers([]);
      }
    };
    load();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setCurrentProject({
        ...project,
        kickoffMeetingConfirmed: !!project.kickoffMeetingConfirmed,
        contractConfirmed: !!project.contractConfirmed,
        managerId: project.managerId || '',
        clientContactName: project.clientContactName || '',
        clientContactEmail: project.clientContactEmail || '',
        clientContactPhone: project.clientContactPhone || '',
        isqCommercialName: project.isqCommercialName || '',
        isqCommercialEmail: project.isqCommercialEmail || '',
        isqCommercialPhone: project.isqCommercialPhone || '',
        proposalPath: project.proposalPath || ''
      });
      setIsEditing(true);
    } else {
      setCurrentProject({
        title: '',
        status: 'kickoff',
        startDate: '',
        endDate: '',
        client: '',
        budget: '',
        clientContactName: '',
        clientContactEmail: '',
        clientContactPhone: '',
        isqCommercialName: '',
        isqCommercialEmail: '',
        isqCommercialPhone: '',
        proposalPath: '',
        kickoffMeetingConfirmed: false,
        contractConfirmed: false,
        managerId: ''
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
    const initial = project ? {
      ...project,
      kickoffMeetingConfirmed: !!project.kickoffMeetingConfirmed,
      contractConfirmed: !!project.contractConfirmed,
      managerId: project.managerId || ''
    } : currentProject;
    setProjectErrors(validateProject(initial));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const next = {
      ...currentProject,
      [name]: name === 'managerId' ? (value ? Number(value) : '') : value
    };
    setCurrentProject(next);
    setProjectErrors(validateProject(next));
  };

  const handleSaveProject = async () => {
    const errs = validateProject(currentProject);
    setProjectErrors(errs);
    if (Object.keys(errs).length > 0) return;
    await ensureLogin();
    const payload = {
      title: currentProject.title,
      status: currentProject.status,
      startDate: currentProject.startDate,
      endDate: currentProject.endDate,
      client: currentProject.client,
      budget: currentProject.budget,
      clientContactName: currentProject.clientContactName || null,
      clientContactEmail: currentProject.clientContactEmail || null,
      clientContactPhone: currentProject.clientContactPhone || null,
      isqCommercialName: currentProject.isqCommercialName || null,
      isqCommercialEmail: currentProject.isqCommercialEmail || null,
      isqCommercialPhone: currentProject.isqCommercialPhone || null,
      proposalPath: currentProject.proposalPath || null,
      kickoffMeetingConfirmed: !!currentProject.kickoffMeetingConfirmed,
      contractConfirmed: !!currentProject.contractConfirmed,
      managerId: currentProject.managerId || null,
    };
    if (isEditing) {
      await updateProject(currentProject.id, payload);
    } else {
      await createProject(payload);
    }
    const data = await fetchProjects();
    setProjects(data);
    handleCloseDialog();
  };

  const validateProject = (p) => {
    const errs = {};
    if (!p.title || String(p.title).trim() === '') errs.title = 'Título obrigatório.';
    if (p.startDate && p.endDate) {
      try {
        const s = new Date(p.startDate);
        const e = new Date(p.endDate);
        if (s > e) errs.dateOrder = 'Data de Início deve ser anterior ou igual à de Término.';
      } catch {}
    }
    return errs;
  };

  const handleDeleteProject = async (id) => {

    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await ensureLogin();
        await deleteProject(id);
      } catch (err) {
        // Se a API responder com erro (ex.: 404), tratamos de forma idempotente
        const msg = err?.response?.data?.message || err?.message || 'Erro desconhecido ao excluir o projeto.';
        // Em caso de falha de rede (sem response), avisar o utilizador
        if (!err?.response) {
          alert(`Falha de rede ao excluir o projeto: ${msg}. Verifique se o backend está a correr.`);
        } else {
          // Mostrar mensagem de erro da API, mas continuar a atualizar a lista
          console.warn('Erro ao excluir projeto:', msg);
        }
      } finally {
        // Atualizar sempre a lista (idempotente): se já não existir, não aparecerá
        const data = await fetchProjects();
        setProjects(data);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return '#FFCE56';
      case 'kickoff': return '#36A2EB';
      case 'execution': return '#FF6384';
      case 'closing': return '#4BC0C0';
      case 'completed': return '#9966FF';
      default: return '#999999';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'planning': return 'Planeamento';
      case 'kickoff': return 'Kick-off';
      case 'execution': return 'Execução';
      case 'closing': return 'Encerramento';
      case 'completed': return 'Concluído';
      default: return 'Desconhecido';
    }
  };

  const formatDateStr = (dateString) => {
    if (!dateString) return '';
    return formatDateUtil(dateString);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Projetos</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Projeto
        </Button>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Início</TableCell>
                <TableCell>Data Fim</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Orçamento</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{project.title}</Typography>
                        <Chip label={`ID ${project.id}`} size="small" variant="outlined" color="default" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusText(project.status)} 
                        size="small" 
                        sx={{ backgroundColor: getStatusColor(project.status), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{formatDateStr(project.startDate)}</TableCell>
                    <TableCell>{formatDateStr(project.endDate)}</TableCell>
                    <TableCell>{project.client}</TableCell>
                    <TableCell>{formatCurrency(project.budget)}</TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="primary" 
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton 
                        color="primary" 
                        onClick={() => navigate(`/projects/${project.id}/dashboard`)}
                        title="Dashboard do Projeto"
                      >
                        <AssessmentIcon />
                      </IconButton>
                      <IconButton 
                        color="primary"
                        onClick={() => handleOpenDialog(project)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error"
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={projects.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Diálogo para adicionar/editar projeto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Projeto' : 'Novo Projeto'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Título do Projeto"
                fullWidth
                value={currentProject.title}
                onChange={handleInputChange}
                required
                error={Boolean(projectErrors.title)}
                helperText={projectErrors.title || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={currentProject.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="kickoff">Kick-off</MenuItem>
                  <MenuItem value="planning">Planeamento</MenuItem>
                  <MenuItem value="execution">Execução</MenuItem>
                  <MenuItem value="closing">Encerramento</MenuItem>
                  <MenuItem value="completed">Concluído</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Reunião de Kick-off</InputLabel>
                <Select
                  value={currentProject.kickoffMeetingConfirmed ? 'sim' : 'nao'}
                  onChange={(e) =>
                    setCurrentProject((prev) => ({
                      ...prev,
                      kickoffMeetingConfirmed: e.target.value === 'sim',
                    }))
                  }
                  label="Reunião de Kick-off"
                >
                  <MenuItem value="sim">Sim</MenuItem>
                  <MenuItem value="nao">Não</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Contrato</InputLabel>
                <Select
                  value={currentProject.contractConfirmed ? 'sim' : 'nao'}
                  onChange={(e) =>
                    setCurrentProject((prev) => ({
                      ...prev,
                      contractConfirmed: e.target.value === 'sim',
                    }))
                  }
                  label="Contrato"
                >
                  <MenuItem value="sim">Sim</MenuItem>
                  <MenuItem value="nao">Não</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="manager-label" shrink>Gestora de Projeto</InputLabel>
                <Select
                  labelId="manager-label"
                  id="manager-select"
                  name="managerId"
                  value={currentProject.managerId === '' ? '' : currentProject.managerId}
                  onChange={handleInputChange}
                  label="Gestora de Projeto"
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === '' || selected === null || typeof selected === 'undefined') {
                      return <em>Selecione a gestora</em>;
                    }
                    const found = users.find((u) => u.id === Number(selected));
                    return found ? `${found.name} (${found.email})` : selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione a gestora</em>
                  </MenuItem>
                  {users
                    .filter((u) => u.role === 'manager')
                    .map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="client"
                label="Cliente"
                fullWidth
                value={currentProject.client}
                onChange={handleInputChange}
                required
                error={Boolean(projectErrors.client)}
                helperText={projectErrors.client || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="startDate"
                label="Data de Início"
                type="date"
                fullWidth
                value={currentProject.startDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(projectErrors.startDate) || Boolean(projectErrors.dateOrder)}
                helperText={projectErrors.startDate ? projectErrors.startDate : (projectErrors.dateOrder || '')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="endDate"
                label="Data de Término"
                type="date"
                fullWidth
                value={currentProject.endDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(projectErrors.dateOrder)}
                helperText={projectErrors.dateOrder || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="budget"
                label="Orçamento (€)"
                type="number"
                fullWidth
                value={currentProject.budget}
                onChange={handleInputChange}
                error={Boolean(projectErrors.budget)}
                helperText={projectErrors.budget || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Contactos do Cliente</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="clientContactName"
                label="Nome do Contacto"
                fullWidth
                value={currentProject.clientContactName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="clientContactEmail"
                label="Email do Contacto"
                type="email"
                fullWidth
                value={currentProject.clientContactEmail}
                onChange={handleInputChange}
                error={Boolean(projectErrors.clientContactEmail)}
                helperText={projectErrors.clientContactEmail || ''}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="clientContactPhone"
                label="Telemóvel do Contacto"
                fullWidth
                value={currentProject.clientContactPhone}
                onChange={handleInputChange}
                error={Boolean(projectErrors.clientContactPhone)}
                helperText={projectErrors.clientContactPhone || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Nome Comercial ISQ</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="isqCommercialName"
                label="Nome"
                fullWidth
                value={currentProject.isqCommercialName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="isqCommercialEmail"
                label="Email"
                type="email"
                fullWidth
                value={currentProject.isqCommercialEmail}
                onChange={handleInputChange}
                error={Boolean(projectErrors.isqCommercialEmail)}
                helperText={projectErrors.isqCommercialEmail || ''}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="isqCommercialPhone"
                label="Telefone"
                fullWidth
                value={currentProject.isqCommercialPhone}
                onChange={handleInputChange}
                error={Boolean(projectErrors.isqCommercialPhone)}
                helperText={projectErrors.isqCommercialPhone || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Proposta</Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                name="proposalPath"
                label="Ficheiro carregado"
                fullWidth
                value={currentProject.proposalPath || ''}
                onChange={() => {}}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" component="label">
                Carregar Proposta (PDF)
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.type !== 'application/pdf') {
                      alert('A proposta deve ser um PDF.');
                      return;
                    }
                    if (file.size > 10 * 1024 * 1024) {
                      alert('O ficheiro é demasiado grande (máx. 10 MB).');
                      return;
                    }
                    await ensureLogin();
                    try {
                      const { path } = await uploadFile(file);
                      if (!path) {
                        alert('Resposta inválida do servidor ao carregar proposta.');
                        return;
                      }
                      setCurrentProject((prev) => ({ ...prev, proposalPath: path }));
                    } catch (err) {
                      const msg = err?.response?.data?.message || err?.message || 'Falha ao carregar proposta.';
                      alert(msg);
                    }
                  }}
                />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveProject} variant="contained" color="primary" disabled={Object.keys(projectErrors).length > 0}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
